import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import InfoTooltip from "../../../components/common/InfoTooltip";
import {
  createWorkspace,
  deleteWorkspacePhoto,
  getWorkspace,
  updateWorkspace,
  uploadWorkspacePhoto,
} from "../../../api/workspaces";
import { getWorkspaceOwnershipRules, updateWorkspaceOwnershipRules } from "../../../api/workspaceCompliance";
import { useAuth } from "../../../context/AuthContext";
import { resolveAssetUrl } from "../../../utils/assetUrl";
import { getCurrentSalonName } from "../../../utils/currentSalon";
import {
  formatWorkspaceMoney,
  getWorkspaceDisplayName,
  getWorkspaceListingPath,
  normalizeWorkspace,
  titleCaseLabel,
} from "../../../utils/workspacePresentation";
import {
  getMarketplaceOperatorSettingsPath,
  getMarketplaceOperatorWorkspacesPath,
  getMarketplaceOperatorWorkspaceDetailPath,
} from "../../../utils/marketplaceOperatorRoutes";
import RentalsShell, { rentalsHeroSecondaryActionClass } from "./RentalsShell";

const steps = [
  "Listing Details",
  "Rent & Availability",
  "Renter Rules",
  "Review & Publish",
];

function getSetupStepFromSearch(search = "") {
  const rawStep = new URLSearchParams(search).get("setupStep");
  const parsed = Number(rawStep);
  if (!Number.isInteger(parsed)) return 0;
  return Math.min(Math.max(parsed, 0), steps.length - 1);
}

const defaultForm = {
  workspaceName: "",
  workspaceType: "chair",
  workspaceCode: "",
  salonLabel: "",
  description: "",
  idealFor: "",
  amenitiesText: "",
  photosText: "",
  visibility: "private",
  termType: "flexible_request",
  startDate: "",
  endDate: "",
  minimumRentalPeriod: "",
  paymentSchedule: "weekly",
  baseRentAmount: "",
  depositAmount: "",
  dueNowRule: "deposit_due_now",
  cancellationNotice: "14 days",
  renewalRules: "Month to month unless a fixed end date is set.",
  terminationRules: "Written notice required before the cancellation deadline.",
  renterOperatesIndependently: true,
  renterControlsPricing: true,
  renterControlsSchedule: true,
  renterOwnsClients: true,
  providerVisibility: "Limited location-support visibility only.",
  houseRules: "",
  warrantyResponsibility: "Renter manages their own service warranties and follow-up.",
  supplyResponsibility: "Renter supplies professional products unless listed as included.",
  brandingRules: "Independent branding allowed inside salon house rules.",
  paymentCollection: "renter",
  restrictedVisibilityRules: "Provider visibility is limited to location support, safety, complaints, and payment workflow.",
  warningsAcknowledged: false,
};

const fieldClass =
  "h-11 w-full min-w-0 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";
const textareaClass =
  "min-h-[110px] w-full min-w-0 resize-y rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

const amenityOptions = [
  "Manicure table",
  "Pedicure chair",
  "Client chair",
  "Technician stool",
  "UV/LED curing lamp",
  "Nail dust collector",
  "Ventilation access",
  "Sink access",
  "Towel service",
  "Locking storage",
  "Retail display shelf",
  "Backbar access",
  "Sterilizer access",
  "Autoclave access",
  "Laundry access",
  "Reception support",
  "Online booking exposure",
  "Wi-Fi",
  "Parking",
  "Break room access",
];

const MAX_WORKSPACE_PHOTOS = 5;
const MAX_WORKSPACE_PHOTO_MB = 5;
const MAX_WORKSPACE_PHOTO_BYTES = MAX_WORKSPACE_PHOTO_MB * 1024 * 1024;
const WORKSPACE_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

const FIELD_TOOLTIPS = {
  salonLocation:
    "The salon or location this workspace belongs to. Use the current salon name unless this listing should be tied to another approved location.",
  workspaceName:
    "The public listing name renters will see. Use a clear name such as Chair 2, Window Suite, or Private Esthetics Room.",
  workspaceType:
    "Choose the physical workspace category that best matches what the renter will use.",
  workspaceCode:
    "Optional internal ID for your team, inventory, or accounting records. Renters do not need this to understand the listing.",
  description:
    "Briefly describe the workspace, location inside the salon, included setup, and anything important a renter should know before requesting it.",
  idealFor:
    "Describe the type of independent professional this space fits best, such as nail tech, lash artist, barber, stylist, or esthetician.",
  amenities:
    "Select included nail salon amenities from the list, then add anything missing in Other amenities.",
  photos:
    "Upload up to 5 photos. Use clear images of the salon, amenities, rental workspace, station setup, storage, lighting, and shared areas.",
  visibility:
    "Private keeps it hidden, draft keeps it unpublished while you prepare it, and public makes it available once publishing requirements are met.",
  termType:
    "Select how renters can book or request the space: fixed dates, recurring schedule, ongoing arrangement, or flexible request.",
  paymentSchedule:
    "Choose how rent is charged. This controls how the base rent amount is interpreted.",
  startDate:
    "First date the workspace can be rented. Leave blank for flexible-request listings that are not tied to a specific start date.",
  endDate:
    "Optional last date for a fixed or temporary rental. Leave blank for ongoing or flexible arrangements.",
  minimumRentalPeriod:
    "Minimum commitment before a renter can request the workspace, such as 1 day, 4 weeks, or 3 months.",
  baseRentAmount:
    "Enter the rent amount before fees or deposits. The payment schedule determines whether this is daily, weekly, monthly, or custom.",
  depositAmount:
    "Optional security deposit or hold amount required before or at approval.",
  dueNowRule:
    "Controls what the renter must pay immediately versus what is collected later after provider confirmation.",
  cancellationNotice:
    "How much notice the renter must give before ending or changing the rental, such as 14 days or 30 days.",
  renewalRules:
    "Explain what happens after the initial term, including month-to-month renewal, fixed renewal periods, or provider review.",
  terminationRules:
    "Explain how either side can end the arrangement and any notice, breach, or cleanup requirements.",
  renterOperatesIndependently:
    "Use this when the renter runs their own business from the space rather than acting as salon staff.",
  renterControlsPricing:
    "Use this when the renter sets their own service prices instead of following salon pricing.",
  renterControlsSchedule:
    "Use this when the renter manages their own client schedule and availability.",
  renterOwnsClients:
    "Use this when the renter owns the client relationship and client records for services they provide.",
  providerVisibility:
    "Define what the salon provider can see or access, such as location support, safety issues, payment workflow, or complaints.",
  houseRules:
    "Rules the renter must follow while using the salon, such as cleanliness, noise, shared areas, security, keys, or closing procedures.",
  warrantyResponsibility:
    "State who handles service fixes, refunds, touch-ups, complaints, or follow-up with the renter's clients.",
  supplyResponsibility:
    "State who provides products, tools, towels, disposables, backbar, cleaning supplies, or other operating materials.",
  brandingRules:
    "Explain what independent branding is allowed, including signage, social media, business cards, uniforms, or station displays.",
  paymentCollection:
    "Choose who collects client payments or rent-related payments for this workspace arrangement.",
  restrictedVisibilityRules:
    "Clarify limits on provider access to renter client data, marketing lists, sales records, or private business information.",
};

function parseListText(value = "") {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function serializeListText(items = []) {
  return Array.isArray(items) ? items.join("\n") : "";
}

function uniqueListItems(items = []) {
  const seen = new Set();
  const uniqueItems = [];
  items.forEach((item) => {
    const value = String(item || "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    uniqueItems.push(value);
  });
  return uniqueItems;
}

function parseAmenities(value = "") {
  return parseListText(value).map((line, index) => ({
    key:
      line
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || `amenity-${index + 1}`,
    label: line,
    pricing_type: "included",
    unit_price: 0,
  }));
}

function serializeAmenities(items = []) {
  return Array.isArray(items)
    ? items.map((item) => item?.label || item?.name || item?.key).filter(Boolean).join("\n")
    : "";
}

function splitAmenities(value = "") {
  const selected = [];
  const custom = [];
  const optionSet = new Set(amenityOptions.map((item) => item.toLowerCase()));
  parseListText(value).forEach((item) => {
    const matchingOption = amenityOptions.find(
      (option) => option.toLowerCase() === item.toLowerCase()
    );
    if (matchingOption && optionSet.has(matchingOption.toLowerCase())) {
      if (!selected.includes(matchingOption)) selected.push(matchingOption);
    } else {
      custom.push(item);
    }
  });
  return { selected, customText: custom.join("\n") };
}

function mergeAmenities(selected = [], customText = "") {
  return [...selected, ...parseListText(customText)].join("\n");
}

function createPendingPhoto(file) {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file),
  };
}

function waitForNextPhotoFilenameWindow() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 1100);
  });
}

function fromWorkspace(workspace = {}, ownershipRules = {}) {
  const normalized = normalizeWorkspace(workspace);
  const policy = normalized.policy_set || {};
  const rules = normalized.booking_rules_json || {};
  const agreement = rules.workspace_listing_setup || {};
  return {
    ...defaultForm,
    workspaceName: getWorkspaceDisplayName(normalized),
    workspaceType: normalized.workspace_type || "chair",
    workspaceCode: normalized.workspace_code || "",
    salonLabel: normalized.salon?.name || "",
    description: normalized.description || "",
    idealFor: agreement.idealFor || "",
    amenitiesText: serializeAmenities(normalized.amenity_addons),
    photosText: serializeListText(uniqueListItems(normalized.photos)),
    visibility: normalized.visibility || "private",
    termType: agreement.termType || "flexible_request",
    startDate: agreement.startDate || "",
    endDate: agreement.endDate || "",
    minimumRentalPeriod: agreement.minimumRentalPeriod || policy.minimum_booking_hours || "",
    paymentSchedule: agreement.paymentSchedule || "weekly",
    baseRentAmount: String(normalized.price_per_week ?? normalized.price_per_day ?? normalized.price_per_hour ?? ""),
    depositAmount:
      normalized.deposit_amount === null || normalized.deposit_amount === undefined
        ? ""
        : String(normalized.deposit_amount),
    dueNowRule: agreement.dueNowRule || "deposit_due_now",
    cancellationNotice: agreement.cancellationNotice || `${policy.cancellation_cutoff_hours || 24} hours`,
    renewalRules: agreement.renewalRules || defaultForm.renewalRules,
    terminationRules: agreement.terminationRules || defaultForm.terminationRules,
    renterOperatesIndependently:
      agreement.renterOperatesIndependently !== undefined
        ? Boolean(agreement.renterOperatesIndependently)
        : true,
    renterControlsPricing:
      agreement.renterControlsPricing !== undefined
        ? Boolean(agreement.renterControlsPricing)
        : true,
    renterControlsSchedule:
      agreement.renterControlsSchedule !== undefined
        ? Boolean(agreement.renterControlsSchedule)
        : true,
    renterOwnsClients:
      ownershipRules.default_client_ownership_mode
        ? ownershipRules.default_client_ownership_mode === "independent_owned"
        : agreement.renterOwnsClients !== undefined
        ? Boolean(agreement.renterOwnsClients)
        : true,
    providerVisibility: agreement.providerVisibility || defaultForm.providerVisibility,
    houseRules: policy.house_rules_text || "",
    warrantyResponsibility: agreement.warrantyResponsibility || defaultForm.warrantyResponsibility,
    supplyResponsibility: agreement.supplyResponsibility || defaultForm.supplyResponsibility,
    brandingRules: agreement.brandingRules || defaultForm.brandingRules,
    paymentCollection: ownershipRules.payment_collector || agreement.paymentCollection || "renter",
    restrictedVisibilityRules:
      agreement.restrictedVisibilityRules || defaultForm.restrictedVisibilityRules,
    warningsAcknowledged: Boolean(agreement.warningsAcknowledged),
  };
}

function buildWarnings(form) {
  const warnings = [];
  if (form.renterOperatesIndependently && !form.renterControlsPricing) {
    warnings.push("Independent professional terms conflict with provider-controlled service pricing.");
  }
  if (form.renterOperatesIndependently && !form.renterControlsSchedule) {
    warnings.push("Independent professional terms conflict with provider-controlled fixed scheduling.");
  }
  if (form.renterOwnsClients && /full crm|marketing control|provider owns/i.test(form.restrictedVisibilityRules)) {
    warnings.push("Client ownership terms conflict with full provider CRM or marketing control.");
  }
  if (form.renterOperatesIndependently && /no branding|provider branding only/i.test(form.brandingRules)) {
    warnings.push("Independent professional terms conflict with blocking independent branding.");
  }
  if (form.visibility === "public" && (!form.workspaceName || !form.baseRentAmount || !form.paymentSchedule)) {
    warnings.push("Public visibility requires workspace details and agreement terms before publishing.");
  }
  return warnings;
}

function buildPayload(form, salonId, publish = false) {
  const amount = Number(form.baseRentAmount || 0);
  const useWeekly = form.paymentSchedule === "weekly";
  const useMonthly = form.paymentSchedule === "monthly";
  const useDaily = form.paymentSchedule === "daily" || form.paymentSchedule === "upfront";
  return {
    salon_id: salonId,
    booth_name: form.workspaceName,
    workspace_code: form.workspaceCode || null,
    description: form.description || null,
    workspace_type: form.workspaceType,
    rental_status: publish ? "active" : "draft",
    marketplace_status: publish ? "published" : "hidden",
    visibility: publish ? "public" : form.visibility,
    booking_mode: form.termType === "flexible_request" ? "request" : "public",
    price_per_hour: useDaily || useWeekly || useMonthly ? 0 : amount,
    price_per_day: useDaily ? amount : null,
    price_per_week: useWeekly || useMonthly ? amount : null,
    available_start_time: null,
    available_end_time: null,
    daily_hours: null,
    buffer_minutes: 15,
    minimum_notice_hours: 0,
    maximum_advance_days: 180,
    requires_approval: true,
    is_featured: false,
    is_available: true,
    currency: "CAD",
    deposit_amount: form.depositAmount === "" ? null : Number(form.depositAmount),
    cleaning_fee: null,
    booking_rules_json: {
      workspace_listing_setup: {
        idealFor: form.idealFor,
        termType: form.termType,
        startDate: form.startDate,
        endDate: form.endDate,
        minimumRentalPeriod: form.minimumRentalPeriod,
        paymentSchedule: form.paymentSchedule,
        dueNowRule: form.dueNowRule,
        cancellationNotice: form.cancellationNotice,
        renewalRules: form.renewalRules,
        terminationRules: form.terminationRules,
        renterOperatesIndependently: form.renterOperatesIndependently,
        renterControlsPricing: form.renterControlsPricing,
        renterControlsSchedule: form.renterControlsSchedule,
        renterOwnsClients: form.renterOwnsClients,
        providerVisibility: form.providerVisibility,
        warrantyResponsibility: form.warrantyResponsibility,
        supplyResponsibility: form.supplyResponsibility,
        brandingRules: form.brandingRules,
        paymentCollection: form.paymentCollection,
        restrictedVisibilityRules: form.restrictedVisibilityRules,
        warningsAcknowledged: form.warningsAcknowledged,
      },
    },
    photos: uniqueListItems(parseListText(form.photosText)),
    amenity_addons: parseAmenities(form.amenitiesText),
    policy_set: {
      name: "Workspace Rental Agreement",
      cancellation_policy_text: form.cancellationNotice,
      house_rules_text: form.houseRules,
      cancellation_cutoff_hours: Number.parseInt(form.cancellationNotice, 10) || 24,
      minimum_booking_hours: form.minimumRentalPeriod ? Number(form.minimumRentalPeriod) || null : null,
      maximum_booking_days: null,
      check_in_notes: null,
    },
    pricing_rules: [],
  };
}

function InputLabel({ label, tooltip, children, as = "label" }) {
  const Wrapper = as;
  return (
    <Wrapper className="grid min-w-0 gap-2">
      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
        <span className="min-w-0 break-words">{label}</span>
        {tooltip ? (
          <InfoTooltip text={tooltip} label={label} placement="bottom" maxWidth={300} />
        ) : null}
      </span>
      {children}
    </Wrapper>
  );
}

function ToggleRow({ label, tooltip, checked, onChange }) {
  return (
    <label className="flex min-w-0 items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
      <span className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 break-words">{label}</span>
        {tooltip ? (
          <InfoTooltip text={tooltip} label={label} placement="bottom" maxWidth={300} />
        ) : null}
      </span>
      <input
        className="shrink-0"
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export default function WorkspaceEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, currentSalon } = useAuth();
  const fileInputRef = useRef(null);
  const pendingPhotosRef = useRef([]);
  const currentSalonName =
    currentSalon?.name || currentSalon?.salon_name || getCurrentSalonName(user);
  const salonId =
    currentSalon?.id ??
    currentSalon?.salon_id ??
    user?.salon_id ??
    (typeof window !== "undefined" ? Number(window.localStorage.getItem("salon_id")) || null : null);
  const isEditing = Boolean(id && id !== "new");
  const [activeStep, setActiveStep] = useState(() =>
    getSetupStepFromSearch(location.search)
  );
  const [form, setForm] = useState(defaultForm);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [removingPhotoUrls, setRemovingPhotoUrls] = useState([]);

  useEffect(() => {
    const requestedStep = getSetupStepFromSearch(location.search);
    setActiveStep((current) => (current === requestedStep ? current : requestedStep));
  }, [location.search]);

  useEffect(() => {
    if (!isEditing || !token) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      getWorkspace(id, token),
      getWorkspaceOwnershipRules(id, token).catch(() => null),
    ])
      .then(([workspaceRow, ownershipRules]) => {
        if (!active) return;
        const normalized = normalizeWorkspace(workspaceRow);
        setWorkspace(normalized);
        setForm(fromWorkspace(normalized, ownershipRules || {}));
      })
      .catch((err) => {
        if (active) setError(err.message || "Unable to load workspace.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, isEditing, token]);

  useEffect(() => {
    if (isEditing || !currentSalonName) return;
    setForm((prev) =>
      prev.salonLabel === currentSalonName
        ? prev
        : { ...prev, salonLabel: currentSalonName }
    );
  }, [currentSalonName, isEditing]);

  useEffect(() => {
    pendingPhotosRef.current = pendingPhotos;
  }, [pendingPhotos]);

  useEffect(
    () => () => {
      pendingPhotosRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    },
    []
  );

  const warnings = useMemo(() => buildWarnings(form), [form]);
  const readiness = useMemo(
    () => [
      {
        label: "Listing details complete",
        complete: Boolean(form.workspaceName && form.workspaceType && form.description),
        stepIndex: 0,
        action: "Edit details",
      },
      {
        label: "Photos uploaded",
        complete: parseListText(form.photosText).length > 0,
        stepIndex: 0,
        action: "Add photos",
      },
      {
        label: "Rent and payment terms complete",
        complete: Boolean(form.baseRentAmount && form.paymentSchedule && form.termType),
        stepIndex: 1,
        action: "Edit rent terms",
      },
      {
        label: "Availability complete",
        complete: Boolean(form.startDate || form.termType === "flexible_request"),
        stepIndex: 1,
        action: "Edit availability",
      },
      {
        label: "Renter rules complete",
        complete: Boolean(form.houseRules && form.providerVisibility),
        stepIndex: 2,
        action: "Edit renter terms",
      },
      {
        label: "Rental agreement summary ready",
        complete: warnings.length === 0 || form.warningsAcknowledged,
        stepIndex: 3,
        action: "Review agreement",
      },
      {
        label: "Setup checks reviewed",
        complete: warnings.length === 0 || form.warningsAcknowledged,
        stepIndex: 3,
        action: "Review checks",
      },
      {
        label: "Visibility set to public",
        complete: form.visibility === "public",
        stepIndex: 0,
        action: "Change visibility",
      },
      {
        label: "Business rentals enabled",
        complete: true,
        settingsPath: getMarketplaceOperatorSettingsPath(),
        action: "Open settings",
      },
    ],
    [form, warnings.length]
  );
  const canPublish = readiness.every((item) => item.complete);
  const previewListingPath = useMemo(() => {
    if (!workspace?.id) return "";
    const returnSearch = new URLSearchParams(location.search);
    returnSearch.set("setupStep", String(activeStep));
    const serializedReturnSearch = returnSearch.toString();
    const returnTo = `${location.pathname}${
      serializedReturnSearch ? `?${serializedReturnSearch}` : ""
    }${location.hash || ""}`;
    const query = new URLSearchParams({
      preview: "1",
      returnTo,
    });
    return `/workspaces/${workspace.id}?${query.toString()}`;
  }, [activeStep, location.hash, location.pathname, location.search, workspace?.id]);
  const amenityState = useMemo(
    () => splitAmenities(form.amenitiesText),
    [form.amenitiesText]
  );
  const savedPhotos = useMemo(
    () => uniqueListItems(parseListText(form.photosText)),
    [form.photosText]
  );
  const totalPhotoCount = savedPhotos.length + pendingPhotos.length;

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleReadinessItemClick = (item) => {
    if (item?.settingsPath) {
      navigate(item.settingsPath);
      return;
    }
    if (Number.isInteger(item?.stepIndex)) {
      setActiveStep(item.stepIndex);
      window.requestAnimationFrame(() => {
        try {
          window.scrollTo?.({ top: 0, behavior: "smooth" });
        } catch {
          // Some test and embedded browser environments do not implement smooth scrolling.
        }
      });
    }
  };

  const updateSelectedAmenities = (amenity, checked) => {
    const nextSelected = checked
      ? [...amenityState.selected, amenity]
      : amenityState.selected.filter((item) => item !== amenity);
    setField("amenitiesText", mergeAmenities(nextSelected, amenityState.customText));
  };

  const updateCustomAmenities = (value) => {
    setField("amenitiesText", mergeAmenities(amenityState.selected, value));
  };

  const removeSavedPhoto = async (url) => {
    const nextPhotos = savedPhotos.filter((item) => item !== url);
    if (!workspace?.id) {
      setField("photosText", serializeListText(nextPhotos));
      return;
    }

    setPhotoError("");
    setRemovingPhotoUrls((prev) => [...prev, url]);
    try {
      const saved = await deleteWorkspacePhoto(workspace.id, url, token);
      setWorkspace(saved);
      setForm((prev) => ({
        ...prev,
        photosText: serializeListText(uniqueListItems(saved.photos || [])),
      }));
      setFlash("Photo removed.");
    } catch (err) {
      setPhotoError(err.message || "Unable to remove workspace photo.");
    } finally {
      setRemovingPhotoUrls((prev) => prev.filter((item) => item !== url));
    }
  };

  const removePendingPhoto = (id) => {
    setPendingPhotos((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
    setPhotoError("");
  };

  const handlePhotoSelection = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const availableSlots = MAX_WORKSPACE_PHOTOS - totalPhotoCount;
    if (availableSlots <= 0) {
      setPhotoError(`You can upload up to ${MAX_WORKSPACE_PHOTOS} photos.`);
      return;
    }

    const selectedFiles = [];
    for (const file of files) {
      if (selectedFiles.length >= availableSlots) break;
      if (!WORKSPACE_PHOTO_TYPES.includes(file.type)) {
        setPhotoError("Use JPG, PNG, or WebP images only.");
        return;
      }
      if (file.size > MAX_WORKSPACE_PHOTO_BYTES) {
        setPhotoError(`Each photo must be ${MAX_WORKSPACE_PHOTO_MB} MB or smaller.`);
        return;
      }
      selectedFiles.push(file);
    }

    if (files.length > availableSlots) {
      setPhotoError(`Only ${availableSlots} more photo${availableSlots === 1 ? "" : "s"} can be added.`);
    } else {
      setPhotoError("");
    }
    setPendingPhotos((prev) => [...prev, ...selectedFiles.map(createPendingPhoto)]);
  };

  const saveWorkspace = async (publish = false) => {
    if (!token || !salonId) {
      setError("A salon context is required before saving a workspace.");
      return null;
    }
    const publishWorkspace = Boolean(publish);
    if (publishWorkspace && !canPublish) {
      setError("Complete the readiness checklist before publishing this workspace.");
      return null;
    }
    setSaving(true);
    setError("");
    setFlash("");
    setPhotoError("");
    try {
      const payload = buildPayload(form, salonId, publishWorkspace);
      let saved = workspace?.id
        ? await updateWorkspace(workspace.id, payload, token)
        : await createWorkspace(payload, token);
      if (pendingPhotos.length) {
        for (const [index, item] of pendingPhotos.entries()) {
          if (index > 0) {
            await waitForNextPhotoFilenameWindow();
          }
          saved = await uploadWorkspacePhoto(saved.id, item.file, token);
        }
        pendingPhotos.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setPendingPhotos([]);
        setForm((prev) => ({
          ...prev,
          photosText: serializeListText(uniqueListItems(saved.photos || [])),
        }));
      }
      await updateWorkspaceOwnershipRules(
        saved.id,
        {
          default_occupancy_mode: "independent_owner",
          default_client_ownership_mode: form.renterOwnsClients ? "independent_owned" : "shared",
          payment_collector: form.paymentCollection,
          requires_agreement_acknowledgement: true,
          allow_renter_guest_checkout: false,
        },
        token
      );
      setWorkspace(saved);
      setFlash(publishWorkspace ? "Workspace published." : "Workspace saved.");
      if (publishWorkspace) {
        navigate(getWorkspaceListingPath(saved), { replace: true });
      } else if (!isEditing) {
        navigate(getMarketplaceOperatorWorkspaceDetailPath(saved.id), { replace: true });
      }
      return saved;
    } catch (err) {
      setError(err.message || "Unable to save workspace.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <InputLabel label="Salon / location" tooltip={FIELD_TOOLTIPS.salonLocation}>
            <input
              className={`${fieldClass} bg-slate-50 text-slate-600`}
              value={form.salonLabel || currentSalonName || ""}
              readOnly
              placeholder="Current salon"
            />
          </InputLabel>
          <InputLabel label="Workspace name" tooltip={FIELD_TOOLTIPS.workspaceName}>
            <input className={fieldClass} value={form.workspaceName} onChange={(event) => setField("workspaceName", event.target.value)} />
          </InputLabel>
          <InputLabel label="Workspace type" tooltip={FIELD_TOOLTIPS.workspaceType}>
            <select className={fieldClass} value={form.workspaceType} onChange={(event) => setField("workspaceType", event.target.value)}>
              <option value="chair">Chair</option>
              <option value="suite">Suite</option>
              <option value="room">Room</option>
              <option value="station">Station</option>
              <option value="open_floor">Open floor</option>
            </select>
          </InputLabel>
          <InputLabel label="Internal reference number (optional)" tooltip={FIELD_TOOLTIPS.workspaceCode}>
            <input className={fieldClass} value={form.workspaceCode} onChange={(event) => setField("workspaceCode", event.target.value)} />
          </InputLabel>
          <InputLabel label="Short description" tooltip={FIELD_TOOLTIPS.description}>
            <textarea className={textareaClass} value={form.description} onChange={(event) => setField("description", event.target.value)} />
          </InputLabel>
          <InputLabel label="Ideal for" tooltip={FIELD_TOOLTIPS.idealFor}>
            <textarea className={textareaClass} value={form.idealFor} onChange={(event) => setField("idealFor", event.target.value)} />
          </InputLabel>
          <InputLabel label="Amenities included" tooltip={FIELD_TOOLTIPS.amenities} as="div">
            <div className="rounded-2xl border border-slate-300 bg-white shadow-sm">
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-800">
                  <span>
                    {amenityState.selected.length
                      ? `${amenityState.selected.length} selected`
                      : "Select amenities"}
                  </span>
                  <span className="text-xs text-slate-500 group-open:hidden">Open</span>
                  <span className="hidden text-xs text-slate-500 group-open:inline">Close</span>
                </summary>
                <div className="grid gap-2 border-t border-slate-200 px-4 py-3 sm:grid-cols-2">
                  {amenityOptions.map((amenity) => (
                    <label
                      key={amenity}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={amenityState.selected.includes(amenity)}
                        onChange={(event) => updateSelectedAmenities(amenity, event.target.checked)}
                      />
                      <span>{amenity}</span>
                    </label>
                  ))}
                </div>
              </details>
              <div className="border-t border-slate-200 px-4 py-3">
                <label className="grid gap-2 text-sm font-semibold text-slate-700">
                  Other amenities
                  <textarea
                    className="min-h-[84px] w-full min-w-0 resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    value={amenityState.customText}
                    onChange={(event) => updateCustomAmenities(event.target.value)}
                    placeholder={"Add anything not listed, one per line"}
                  />
                </label>
              </div>
            </div>
          </InputLabel>
          <InputLabel label="Photos" tooltip={FIELD_TOOLTIPS.photos} as="div">
            <div className="space-y-3 rounded-2xl border border-slate-300 bg-white px-4 py-3 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-600">
                  Upload salon, amenity, and rental workspace photos. JPG, PNG, or WebP. Max {MAX_WORKSPACE_PHOTO_MB} MB each.
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={totalPhotoCount >= MAX_WORKSPACE_PHOTOS}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Upload Photos
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={WORKSPACE_PHOTO_TYPES.join(",")}
                multiple
                onChange={handlePhotoSelection}
                className="hidden"
              />
              {photoError ? <p className="text-sm font-semibold text-rose-600">{photoError}</p> : null}
              <div className="grid gap-3 sm:grid-cols-2">
                {savedPhotos.map((url) => (
                  <div key={url} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2">
                    <img
                      src={resolveAssetUrl(url)}
                      alt="Workspace"
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1 text-xs text-slate-600">
                      <div className="truncate">{url}</div>
                      <button
                        type="button"
                        onClick={() => removeSavedPhoto(url)}
                        disabled={removingPhotoUrls.includes(url)}
                        className="mt-1 font-semibold text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {removingPhotoUrls.includes(url) ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>
                ))}
                {pendingPhotos.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2">
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1 text-xs text-slate-600">
                      <div className="truncate">{item.file.name}</div>
                      <div>{(item.file.size / 1024 / 1024).toFixed(1)} MB - uploads on save</div>
                      <button
                        type="button"
                        onClick={() => removePendingPhoto(item.id)}
                        className="mt-1 font-semibold text-rose-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {!totalPhotoCount ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                  Add up to {MAX_WORKSPACE_PHOTOS} photos. Good choices include the salon exterior/interior, rental station, manicure table, pedicure area, storage, lighting, sink access, and included amenities.
                </div>
              ) : null}
            </div>
          </InputLabel>
          <InputLabel label="Visibility" tooltip={FIELD_TOOLTIPS.visibility}>
            <select className={fieldClass} value={form.visibility} onChange={(event) => setField("visibility", event.target.value)}>
              <option value="private">Private</option>
              <option value="draft">Draft</option>
              <option value="public">Public</option>
            </select>
          </InputLabel>
        </div>
      );
    }
    if (activeStep === 1) {
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <InputLabel label="How can this workspace be rented?" tooltip={FIELD_TOOLTIPS.termType}>
            <select className={fieldClass} value={form.termType} onChange={(event) => setField("termType", event.target.value)}>
              <option value="fixed_date_range">Fixed date range</option>
              <option value="recurring_schedule">Recurring schedule</option>
              <option value="ongoing_until_cancelled">Ongoing until cancelled</option>
              <option value="flexible_request">Flexible request</option>
            </select>
          </InputLabel>
          <InputLabel label="Payment schedule" tooltip={FIELD_TOOLTIPS.paymentSchedule}>
            <select className={fieldClass} value={form.paymentSchedule} onChange={(event) => setField("paymentSchedule", event.target.value)}>
              <option value="upfront">Upfront</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom</option>
            </select>
          </InputLabel>
          <InputLabel label="Start date" tooltip={FIELD_TOOLTIPS.startDate}>
            <input className={fieldClass} type="date" value={form.startDate} onChange={(event) => setField("startDate", event.target.value)} />
          </InputLabel>
          <InputLabel label="Optional end date" tooltip={FIELD_TOOLTIPS.endDate}>
            <input className={fieldClass} type="date" value={form.endDate} onChange={(event) => setField("endDate", event.target.value)} />
          </InputLabel>
          <InputLabel label="Minimum rental period" tooltip={FIELD_TOOLTIPS.minimumRentalPeriod}>
            <input className={fieldClass} value={form.minimumRentalPeriod} onChange={(event) => setField("minimumRentalPeriod", event.target.value)} placeholder="Example: 1 day, 4 weeks, 3 months" />
          </InputLabel>
          <InputLabel label="Base rent amount" tooltip={FIELD_TOOLTIPS.baseRentAmount}>
            <input className={fieldClass} type="number" step="0.01" value={form.baseRentAmount} onChange={(event) => setField("baseRentAmount", event.target.value)} />
          </InputLabel>
          <InputLabel label="Deposit amount" tooltip={FIELD_TOOLTIPS.depositAmount}>
            <input className={fieldClass} type="number" step="0.01" value={form.depositAmount} onChange={(event) => setField("depositAmount", event.target.value)} />
          </InputLabel>
          <InputLabel label="Due now / due later rules" tooltip={FIELD_TOOLTIPS.dueNowRule}>
            <select className={fieldClass} value={form.dueNowRule} onChange={(event) => setField("dueNowRule", event.target.value)}>
              <option value="deposit_due_now">Deposit due now</option>
              <option value="first_period_due_now">First period due now</option>
              <option value="provider_confirms_later">Provider confirms later</option>
            </select>
          </InputLabel>
          <InputLabel label="Cancellation notice" tooltip={FIELD_TOOLTIPS.cancellationNotice}>
            <input className={fieldClass} value={form.cancellationNotice} onChange={(event) => setField("cancellationNotice", event.target.value)} />
          </InputLabel>
          <InputLabel label="Renewal rules" tooltip={FIELD_TOOLTIPS.renewalRules}>
            <textarea className={textareaClass} value={form.renewalRules} onChange={(event) => setField("renewalRules", event.target.value)} />
          </InputLabel>
          <InputLabel label="Termination rules" tooltip={FIELD_TOOLTIPS.terminationRules}>
            <textarea className={textareaClass} value={form.terminationRules} onChange={(event) => setField("terminationRules", event.target.value)} />
          </InputLabel>
        </div>
      );
    }
    if (activeStep === 2) {
      return (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <ToggleRow label="Renter operates independently" tooltip={FIELD_TOOLTIPS.renterOperatesIndependently} checked={form.renterOperatesIndependently} onChange={(value) => setField("renterOperatesIndependently", value)} />
            <ToggleRow label="Renter controls own service pricing" tooltip={FIELD_TOOLTIPS.renterControlsPricing} checked={form.renterControlsPricing} onChange={(value) => setField("renterControlsPricing", value)} />
            <ToggleRow label="Renter controls own client schedule" tooltip={FIELD_TOOLTIPS.renterControlsSchedule} checked={form.renterControlsSchedule} onChange={(value) => setField("renterControlsSchedule", value)} />
            <ToggleRow label="Renter owns client relationship" tooltip={FIELD_TOOLTIPS.renterOwnsClients} checked={form.renterOwnsClients} onChange={(value) => setField("renterOwnsClients", value)} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <InputLabel label="Provider visibility rules" tooltip={FIELD_TOOLTIPS.providerVisibility}>
              <textarea className={textareaClass} value={form.providerVisibility} onChange={(event) => setField("providerVisibility", event.target.value)} />
            </InputLabel>
            <InputLabel label="House rules" tooltip={FIELD_TOOLTIPS.houseRules}>
              <textarea className={textareaClass} value={form.houseRules} onChange={(event) => setField("houseRules", event.target.value)} />
            </InputLabel>
            <InputLabel label="Warranty / follow-up responsibility" tooltip={FIELD_TOOLTIPS.warrantyResponsibility}>
              <textarea className={textareaClass} value={form.warrantyResponsibility} onChange={(event) => setField("warrantyResponsibility", event.target.value)} />
            </InputLabel>
            <InputLabel label="Product / supply responsibility" tooltip={FIELD_TOOLTIPS.supplyResponsibility}>
              <textarea className={textareaClass} value={form.supplyResponsibility} onChange={(event) => setField("supplyResponsibility", event.target.value)} />
            </InputLabel>
            <InputLabel label="Branding rules" tooltip={FIELD_TOOLTIPS.brandingRules}>
              <textarea className={textareaClass} value={form.brandingRules} onChange={(event) => setField("brandingRules", event.target.value)} />
            </InputLabel>
            <InputLabel label="Payment collection responsibility" tooltip={FIELD_TOOLTIPS.paymentCollection}>
              <select className={fieldClass} value={form.paymentCollection} onChange={(event) => setField("paymentCollection", event.target.value)}>
                <option value="renter">Independent professional</option>
                <option value="salon">Workspace provider</option>
                <option value="platform">Platform</option>
              </select>
            </InputLabel>
            <InputLabel label="Restricted visibility rules" tooltip={FIELD_TOOLTIPS.restrictedVisibilityRules}>
              <textarea className={textareaClass} value={form.restrictedVisibilityRules} onChange={(event) => setField("restrictedVisibilityRules", event.target.value)} />
            </InputLabel>
          </div>
        </div>
      );
    }
    if (activeStep === 3) {
      return (
        <div className="grid gap-5 lg:grid-cols-[0.95fr,1.05fr]">
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="text-lg font-semibold text-slate-900">Readiness Checklist</h3>
            <p className="mt-2 text-sm text-slate-600">
              Select any item to jump to the place where it can be reviewed or fixed.
            </p>
            <div className="mt-4 space-y-2">
              {readiness.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleReadinessItemClick(item)}
                  className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                >
                  <span className="min-w-0">
                    <span className="block font-semibold text-slate-800">{item.label}</span>
                    <span className="mt-0.5 block text-xs font-medium text-slate-500 group-hover:text-slate-700">
                      {item.action}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                      item.complete
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-rose-50 text-rose-700"
                    }`}
                  >
                    {item.complete ? "Complete" : "Needs attention"}
                  </span>
                </button>
              ))}
            </div>
          </section>
          <div className="space-y-5">
            <section className="rounded-3xl border border-slate-200 bg-white p-5">
              <h3 className="text-lg font-semibold text-slate-900">Listing Preview</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <p className="text-2xl font-semibold text-slate-950">{form.workspaceName || "Untitled workspace"}</p>
                <p>{form.description || "No description added yet."}</p>
                <p><span className="font-semibold text-slate-900">Workspace type:</span> {titleCaseLabel(form.workspaceType)}</p>
                <p><span className="font-semibold text-slate-900">Rent:</span> {formatWorkspaceMoney(form.baseRentAmount)} / {titleCaseLabel(form.paymentSchedule)}</p>
                <p><span className="font-semibold text-slate-900">Term:</span> {titleCaseLabel(form.termType)}</p>
                <p><span className="font-semibold text-slate-900">Visibility:</span> {titleCaseLabel(form.visibility)}</p>
              </div>
            </section>
            <section className="rounded-3xl border border-slate-200 bg-white p-5">
              <h3 className="text-lg font-semibold text-slate-900">Rental Agreement Summary</h3>
              <dl className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
                <div><dt className="font-semibold text-slate-900">Workspace</dt><dd>{form.workspaceName || "Untitled workspace"}</dd></div>
                <div><dt className="font-semibold text-slate-900">Rent</dt><dd>{formatWorkspaceMoney(form.baseRentAmount)} / {titleCaseLabel(form.paymentSchedule)}</dd></div>
                <div><dt className="font-semibold text-slate-900">Deposit</dt><dd>{form.depositAmount ? formatWorkspaceMoney(form.depositAmount) : "Not required"}</dd></div>
                <div><dt className="font-semibold text-slate-900">Client ownership</dt><dd>{form.renterOwnsClients ? "Independent professional owns client relationship" : "Shared exception required"}</dd></div>
                <div><dt className="font-semibold text-slate-900">Salon visibility</dt><dd>{form.providerVisibility}</dd></div>
                <div><dt className="font-semibold text-slate-900">Service follow-up</dt><dd>{form.warrantyResponsibility}</dd></div>
                <div className="md:col-span-2"><dt className="font-semibold text-slate-900">Cancellation / termination</dt><dd>{form.cancellationNotice}; {form.terminationRules}</dd></div>
              </dl>
            </section>
            <section className="rounded-3xl border border-slate-200 bg-white p-5">
              <h3 className="text-lg font-semibold text-slate-900">Setup Checks</h3>
              <div className="mt-4 space-y-3">
                {warnings.length ? (
                  warnings.map((warning) => (
                    <div key={warning} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      {warning}
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    No serious contradictions detected.
                  </div>
                )}
                {warnings.length ? (
                  <ToggleRow label="Setup checks reviewed and acknowledged" checked={form.warningsAcknowledged} onChange={(value) => setField("warningsAcknowledged", value)} />
                ) : null}
              </div>
            </section>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <RentalsShell
      title="Rental Listing Setup"
      description="Set up the workspace, rent terms, renter rules, and rental agreement summary in one guided flow."
      actions={
        <Link to={getMarketplaceOperatorWorkspacesPath()} className={rentalsHeroSecondaryActionClass}>
          Back to Workspaces
        </Link>
      }
    >
      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {flash ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{flash}</div> : null}
      {loading ? <p className="text-sm text-slate-600">Loading workspace setup...</p> : null}

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex flex-wrap gap-2">
            {steps.map((step, index) => (
              <button
                key={step}
                type="button"
                onClick={() => setActiveStep(index)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeStep === index
                    ? "bg-rose-500 text-white"
                    : "border border-slate-300 text-slate-700 hover:border-slate-400"
                }`}
              >
                {index + 1}. {step}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-6 px-6 py-6">
          <h2 className="text-2xl font-semibold text-slate-900">{steps[activeStep]}</h2>
          {renderStep()}
          <div className="flex flex-wrap justify-between gap-3 border-t border-slate-200 pt-5">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => saveWorkspace(false)}
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              {previewListingPath ? (
                <Link
                  to={previewListingPath}
                  className="rounded-full border border-sky-300 px-5 py-2.5 text-sm font-semibold text-sky-700 hover:border-sky-400"
                >
                  Preview Listing
                </Link>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={activeStep === 0}
                onClick={() => setActiveStep((step) => Math.max(0, step - 1))}
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Back
              </button>
              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setActiveStep((step) => Math.min(steps.length - 1, step + 1))}
                  className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  disabled={saving || !canPublish}
                  onClick={() => saveWorkspace(true)}
                  className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "Publishing..." : "Publish Workspace"}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </RentalsShell>
  );
}
